import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ChangeHistory } from '@/common/components/ChangeHistory'
import { FormTransformers } from '@/common/utils/formTransformers'
import { Checklist } from '@/features/privateCompanyLeadsFunnel/components/Checklist'
import { AddressSection } from '@/features/publicCompanyLeadsFunnel/components/AddressSection'
import { BiddingInformationSection } from '@/features/publicCompanyLeadsFunnel/components/BiddingInformationSection'
import { GeneralInformationSection } from '@/features/publicCompanyLeadsFunnel/components/GeneralInformationSection'
import { GenericDocumentsSection } from '@/features/publicCompanyLeadsFunnel/components/GenericDocumentsSection'
import { LeadHeader } from '@/features/publicCompanyLeadsFunnel/components/LeadHeader'
import { ResponsibleSection } from '@/features/publicCompanyLeadsFunnel/components/ResponsibleSection'
import {
  stateStrategy,
  translateStateStrategy,
} from '@/features/publicCompanyLeadsFunnel/constants/stateStrategy.constant'
import { useAdvancePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useAdvancePublicCompanyLead'
import usePublicCompanyLeadForm from '@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLeadForm'
import { useUpdatePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLead'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { PublicCompanyLeadData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useUpdatePublicCompanyLeadManagers } from '../../hooks/useUpdatePublicCompanyLeadManagers'

const LeadQualifyingForm = ({ id, publicCompanyLead }: { id: string; publicCompanyLead: PublicCompanyLeadData }) => {
  const queryClient = useQueryClient()
  const [file, setFile] = useState<Blob>()
  const [serverError, setServerError] = useState(false)
  const navigate = useNavigate()
  const passedEveryRequirement = publicCompanyLead?.requirement_list_summary?.steps?.every((step) => step.passed)

  const updateLeadOnSuccess = () => {
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', id] })
  }

  const redirectOnSuccess = (path: string) => {
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', id] })
    navigate(path)
  }

  const onError = () => setServerError(true)

  const { mutate: advancePublicCompanyLead, isPending: advancePublicCompanyLeadIsPending } =
    useAdvancePublicCompanyLead({ id, onSuccess: () => redirectOnSuccess('/public-clients'), onError })

  const useFormMethods = usePublicCompanyLeadForm({ publicCompanyLead })

  const { trigger, getValues, setError, clearErrors } = useFormMethods

  const { mutate: updatePublicCompanyLead } = useUpdatePublicCompanyLead({
    id,
    publicCompanyLead,
    file,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateValidationSchema>({ setError }),
  })

  const {
    mutate: updatePublicCompanyLeadManagers,
    isPending: updatePublicCompanyLeadManagersIsPending,
  } = useUpdatePublicCompanyLeadManagers({
    id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<PublicCompanyLeadsFunnelUpdateValidationSchema>({ setError }),
  });

  const submitManagers = async () => {
    updatePublicCompanyLeadManagers(getValues());
  };

  const onSubmit: SubmitHandler<PublicCompanyLeadsFunnelUpdateValidationSchema> = (data) => {
    clearErrors()
    updatePublicCompanyLead(data)
  }

  const submit = async () => {
    const isValid = await trigger()

    if (!isValid) {
      return
    }

    onSubmit(getValues())
  }

  const submittableOnBlur = (
    e: React.FocusEvent<HTMLInputElement, Element>,
    onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void,
  ) => {
    if (e.target.value === '') {
      return
    }
    onBlur(e)
    submit()
  }

  return (
    <FormProvider {...useFormMethods}>
      <form>
        <LeadHeader
          publicCompanyLeadId={id}
          display_name={publicCompanyLead.display_name}
          url={publicCompanyLead.general_information?.company_logo?.url}
          showArchiveButton
          archived_at={publicCompanyLead.archived_at}
          buttons={
            <Button
              name="submit_for_review"
              type="button"
              className={passedEveryRequirement ? 'shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]' : ''}
              variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
              disabled={!passedEveryRequirement}
              onClick={() => advancePublicCompanyLead()}
              isLoading={advancePublicCompanyLeadIsPending}
            >
              Enviar para análise
            </Button>
          }
        />
        <div className="flex gap-6">
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll">
            {serverError && (
              <p className="text-sm text-danger-soft">
                Ocorreu um erro no servidor, cheque os campos e tente novamente
              </p>
            )}
            <ResponsibleSection
              publicCompanyLead={publicCompanyLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={advancePublicCompanyLeadIsPending || updatePublicCompanyLeadManagersIsPending}
            />
            <BiddingInformationSection
              publicCompanyLeadId={id}
              publicCompanyLeadState={publicCompanyLead.state}
              contractId={publicCompanyLead.contract?.id}
              contractFile={publicCompanyLead.contract?.file}
              bidding_information={publicCompanyLead.bidding_information}
              submittableOnBlur={submittableOnBlur}
              submit={submit}
              disabled={advancePublicCompanyLeadIsPending}
            />
            <GeneralInformationSection
              general_information={publicCompanyLead.general_information}
              submittableOnBlur={submittableOnBlur}
              submit={submit}
              file={file}
              setFile={setFile}
              disabled={advancePublicCompanyLeadIsPending}
            />
            <AddressSection
              address={publicCompanyLead.address}
              submit={submit}
              disabled={advancePublicCompanyLeadIsPending}
            />
            <GenericDocumentsSection
              publicCompanyLeadId={id}
              generic_documents={publicCompanyLead.generic_documents}
              disabled={advancePublicCompanyLeadIsPending}
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={publicCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[publicCompanyLead.state as keyof typeof stateStrategy]]}
              Buttons={() => (
                <Button
                  type="button"
                  variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                  disabled={!passedEveryRequirement}
                  onClick={() => advancePublicCompanyLead()}
                  isLoading={advancePublicCompanyLeadIsPending}
                >
                  Enviar para análise
                </Button>
              )}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
            />
            <ChangeHistory logs={publicCompanyLead?.logs || []} />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default LeadQualifyingForm
